import {type Theme} from '@mui/material';
import {
  ForecastGetColumnsEnum,
  type ConcessionMAB,
  type Forecast,
  type Kpi,
  type MABUtilizationPerConcession,
  type NavigationTreePlan,
  type Timeline,
  ActionSpeciesEnum,
} from 'client';
import {reduceTransfersToCageMultipleTransfers as reduceTransfers} from 'components/Charts/helpers';
import {isSunday, isWeekend, nextSunday} from 'date-fns';
import {dateReader, kilosToTons} from 'helpers/UnitsFormatHandler';
import {buildSWRKey, mutateAll} from 'hooks/swr/swrHookFactory';
import {
  ColumnFieldEnum,
  FormSelectionEnum,
  type KPIColumnEnum,
  ResolutionEnum,
  SWRKeyEnum,
  type CageTransferProcessed,
  type CellActionType,
  type IDictionary,
  type MultipleTransfers,
  type TransferProcessed,
  HarvestTimelineColumnsEnum,
} from 'interfaces';
import {mutate} from 'swr';
import {
  type MetricsEnum,
  type CellAction,
  type CellActionCageData,
  type ForecastPoint,
  type ICellConcessionMABData,
  type INumberDictionary,
  type ForecastDataPoint,
} from './interfaces';
import {mutateCage, mutateWithDelay} from 'helpers/refreshHandler';

export const concessionsMABDataHandler = (
  concessionsMABUtilization: MABUtilizationPerConcession,
): IDictionary<INumberDictionary<ICellConcessionMABData>> => {
  const biomassIndex = concessionsMABUtilization.columns.indexOf(ColumnFieldEnum.biomass);
  const MABIndex = concessionsMABUtilization.columns.indexOf(ColumnFieldEnum.MAB);
  const concessionsId = concessionsMABUtilization.query.concessions;

  const concessionsData: IDictionary<INumberDictionary<ICellConcessionMABData>> = {};
  concessionsId.forEach((id) => {
    const rangeData: INumberDictionary<ICellConcessionMABData> = {};
    const indexOfConcession = concessionsMABUtilization.query.concessions.indexOf(id);
    concessionsMABUtilization.data.forEach((dataPoint, index) => {
      const date = concessionsMABUtilization.index.values
        ? dateReader(concessionsMABUtilization.index.values[index]).getTime()
        : new Date().getTime();
      const biomass = Math.round(kilosToTons(dataPoint[indexOfConcession][biomassIndex]));
      const mab = Math.round(kilosToTons(dataPoint[indexOfConcession][MABIndex]));
      const cellData: ICellConcessionMABData = {biomass, mab, delta: biomass - mab};
      rangeData[date] = cellData;
    });
    concessionsData[id] = rangeData;
  });
  return concessionsData;
};

export const mapForecastByColumns = (forecast: Forecast, columns: ForecastGetColumnsEnum[]) => {
  const siteForecastMap = new Map<number, ForecastDataPoint>(
    forecast?.data.map(([dataColumns], dataPointIndex) => {
      const indexedDates = forecast.index.values;
      const forecastDataPoint = columns.reduce<ForecastDataPoint>((dataPoint, column, columnIndex) => {
        dataPoint[column] = dataColumns[columnIndex];
        return dataPoint;
      }, {});

      return [dateReader(indexedDates[dataPointIndex]).getTime(), forecastDataPoint];
    }),
  );

  return siteForecastMap;
};

export const mapCageForecastByColumns = (
  cageIds: string[],
  cagesForecasts: Forecast,
  columns: ForecastGetColumnsEnum[],
) => {
  const cageForecastMap = new Map<string, ForecastPoint[]>(cageIds.map((id) => [id, []]));
  if (cagesForecasts.query.cages) {
    const indexedCages = cagesForecasts.query.cages;
    const indexedDates = cagesForecasts.index.values;
    cagesForecasts.data.reduce((map, cagesDatPoints, dataPointIndex) => {
      cagesDatPoints.forEach((dataColumns, cageIndex) => {
        const cageForecastData = map.get(indexedCages[cageIndex]) as ForecastPoint[];
        const forecastDataPoint = columns.reduce<ForecastDataPoint>((dataPoint, column, columnIndex) => {
          dataPoint[column] = dataColumns[columnIndex];
          return dataPoint;
        }, {});

        cageForecastData.push([dateReader(indexedDates[dataPointIndex]).getTime(), forecastDataPoint]);
      });
      return map;
    }, cageForecastMap);
  }
  return cageForecastMap;
};

export const biomassInForecastDataPoint = (point: ForecastDataPoint) => {
  const {biomass, biomassSalmon, biomassTrout} = point;
  return [biomass, biomassSalmon, biomassTrout].some((value) => value && value > 0);
};

export const harvestVolumeDataHandler = (
  harvestVolume: Timeline,
): INumberDictionary<{biomass?: number; biomassSalmon?: number; biomassTrout?: number}> => {
  const biomassIndex = harvestVolume.columns.indexOf(HarvestTimelineColumnsEnum.biomass);
  const biomassSalmonIndex = harvestVolume.columns.indexOf(HarvestTimelineColumnsEnum.biomassSalmon);
  const biomassTroutIndex = harvestVolume.columns.indexOf(HarvestTimelineColumnsEnum.biomassTrout);

  const rangeData: INumberDictionary<{biomass?: number; biomassSalmon?: number; biomassTrout?: number}> = {};
  const indexOfPlan = 0;
  harvestVolume.data.forEach((dataPoint, index) => {
    const date = harvestVolume.index.values
      ? dateReader(harvestVolume.index.values[index]).getTime()
      : new Date().getTime();

    const biomass = Math.round(kilosToTons(dataPoint[indexOfPlan][biomassIndex]));
    const biomassSalmon = Math.round(kilosToTons(dataPoint[indexOfPlan][biomassSalmonIndex]));
    const biomassTrout = Math.round(kilosToTons(dataPoint[indexOfPlan][biomassTroutIndex]));

    const cellData = {biomass, biomassSalmon, biomassTrout};
    rangeData[date] = cellData;
  });
  return rangeData;
};

export const getForecastBiomassBySpecies = (forecast: {
  biomass?: number;
  biomassSalmon?: number;
  biomassTrout?: number;
}) => {
  return (forecast?.biomassSalmon ?? 0) + (forecast?.biomassTrout ?? 0);
};

export const KPIDataHandler = (kpi: Kpi, column: KPIColumnEnum): INumberDictionary<number> => {
  const columnIndex = kpi.columns.indexOf(column);

  const rangeData: INumberDictionary<number> = {};
  const indexOfPlan = 0;
  kpi.data[indexOfPlan].forEach((dataPoint, index) => {
    const date = kpi.index.values ? dateReader(kpi.index.values[index]).getTime() : new Date().getTime();
    rangeData[date] = kilosToTons(dataPoint[columnIndex]);
  });
  return rangeData;
};

export const mapActionsToRange = <T extends {dateObject: Date}>(
  actions: T[],
  range: Date[],
  resolution: ResolutionEnum,
) => {
  const actionDate = (date: Date) => (resolution === ResolutionEnum.week && !isSunday(date) ? nextSunday(date) : date);
  const actionsMap = range.map<[Date, T[]]>((date) => {
    const dateActions: T[] = actions.filter((action) => actionDate(action.dateObject).getTime() === date.getTime());
    return [date, dateActions];
  });
  return new Map(actionsMap);
};

const mapActionToTypedTupple = (actionType: CellActionType) => (action: CellAction) => ({actionType, action});

const mapActionToActionData = (action: CellAction, actionType: CellActionType): CellActionCageData => {
  const {id, dateObject, cageId} = action;
  if (actionType === FormSelectionEnum.transfer) {
    const transfer = action as CageTransferProcessed;
    if (transfer.fromCageId !== cageId) {
      const allocation = transfer.allocations.find(({toCageId}) => toCageId === cageId);
      if (allocation) {
        const {weight, count, biomass} = allocation;
        return {id, dateObject, cageId, weight, count, biomass, rawActionType: 'transferIn'};
      }
    } else {
      return {...action, rawActionType: 'transferOut'};
    }
  }
  return action;
};

export const buildRangeToMabMap = (dateRange: Date[], siteMab: ConcessionMAB[]) => {
  let siteMabI = 0;
  return new Map(
    dateRange.map<[number, number | undefined]>((date) => {
      if (siteMabI >= siteMab.length) {
        // empty siteMab
        return [date.getTime(), undefined];
      }
      while (siteMabI < siteMab.length - 1) {
        const {fromDate, limit} = siteMab[siteMabI];
        const {fromDate: toDate} = siteMab[siteMabI + 1];

        if (date < dateReader(fromDate)) {
          return [date.getTime(), undefined];
        }
        if (date < dateReader(toDate)) {
          return [date.getTime(), limit];
        }
        siteMabI++;
      }
      // last element
      return [date.getTime(), siteMab[siteMabI].limit];
    }),
  );
};

export const getForecastWeightBySpecies = (
  forecastDataPoint: ForecastDataPoint,
  species: ActionSpeciesEnum = ActionSpeciesEnum.Salmon,
) => {
  const biomass =
    species === ActionSpeciesEnum.Trout ? forecastDataPoint.biomassTrout : forecastDataPoint.biomassSalmon;
  const count = species === ActionSpeciesEnum.Trout ? forecastDataPoint.countTrout : forecastDataPoint.countSalmon;

  if (biomass !== undefined && count !== undefined && count > 0) {
    return biomass / count;
  }

  return 0;
};

export const getUtilizationColorName = (utilization: number) => {
  if (utilization < 90) {
    return 'success';
  }
  if (utilization < 100) {
    return 'warning';
  }
  return 'error';
};

export const cageDataToMap = <T extends {cageId: string}>(map: Map<string, T>, cageData: T) => {
  map.set(cageData.cageId, cageData);
  return map;
};

export const cageDataArrayToMap = <T extends {cageId: string}>(map: Map<string, T[]>, cageData: T) => {
  let actions: T[] = [];
  if (!map.has(cageData.cageId)) {
    map.set(cageData.cageId, actions);
  } else {
    actions = map.get(cageData.cageId) as T[];
  }
  actions.push(cageData);
  return map;
};

const getEmptyTransfers: () => MultipleTransfers<CageTransferProcessed> = () => ({in: [], out: []});
const getCageTransfers = (cageId: string, siteTransfers: TransferProcessed[]) => {
  return siteTransfers
    .map((transfer) => ({...transfer, cageId}))
    .reduce<MultipleTransfers<CageTransferProcessed>>(reduceTransfers(cageId), getEmptyTransfers());
};

const reduceToCageTransfers = (transfers: TransferProcessed[], siteId: string) => {
  return transfers.reduce<CageTransferProcessed[]>((acc, transfer) => {
    if (transfer.fromSiteId === siteId) {
      acc.push({...transfer, cageId: transfer.fromCageId});
    } else {
      transfer.allocations.forEach(({toCageId}) => {
        acc.push({...transfer, cageId: toCageId});
      });
    }

    return acc;
  }, []);
};

const getSiteCageIds = (navigationTree: NavigationTreePlan, siteId: string) =>
  navigationTree?.sites?.find((site) => site.id === siteId)?.cages?.map((cage) => cage.id) ?? [];

export const buildSiteForecastResourceId = (
  siteId: string,
  startDate: Date,
  endDate: Date,
  resolution: ResolutionEnum,
  columns: ForecastGetColumnsEnum[],
) => [[siteId], [], startDate, endDate, resolution, columns];

export const buildCagesForecastResourceId = (
  cageIds: string[],
  startDate: Date,
  endDate: Date,
  resolution: ResolutionEnum,
  columns: ForecastGetColumnsEnum[],
) => [[], cageIds, startDate, endDate, resolution, columns];

export const getLiceColor = (aboveLiceLimit: boolean, theme: Theme): string => {
  return aboveLiceLimit ? theme.palette.error.main : theme.palette.success.main;
};

export const getMortalityColor = (theme: Theme, mortalityRate: number | undefined) => {
  if (mortalityRate === undefined) {
    return theme.palette.text.primary;
  }
  if (mortalityRate > 0.0005) {
    return theme.palette.error.main;
  }
  if (mortalityRate > 0.00025) {
    return theme.palette.text.primary;
  }
  return theme.palette.success.main;
};

export const getGrowthColor = (theme: Theme, relativeGrowthRate: number) => {
  if (relativeGrowthRate < -0.1) {
    return theme.palette.error.main;
  }
  if (relativeGrowthRate > 0.1) {
    return theme.palette.success.main;
  }
  return theme.palette.text.primary;
};

export const highlightWeekend = (date: Date, resolution: ResolutionEnum) => {
  if (resolution === ResolutionEnum.day) {
    return isWeekend(date);
  }
  return false;
};

export const refreshHandler = ({
  siteId,
  cageIds,
  startDate,
  endDate,
  resolution,
  planId,
  columns,
  tableContextMetric,
}: {
  siteId: string;
  cageIds: string[];
  startDate?: Date;
  endDate?: Date;
  resolution: ResolutionEnum;
  planId: string;

  columns: ForecastGetColumnsEnum[];
  tableContextMetric?: MetricsEnum;
}) => {
  if (startDate && endDate) {
    mutate([...buildSWRKey([planId, siteId, startDate, endDate]), SWRKeyEnum.GET_SITE_ACTIONS]);

    cageIds.forEach((cageId) => {
      mutateCage(cageId);
    });
    mutate([
      ...buildSWRKey(buildCagesForecastResourceId(cageIds, startDate, endDate, resolution, columns)),
      SWRKeyEnum.GET_SITE_CAGE_FORECAST,
    ]);
    mutate([
      ...buildSWRKey(buildSiteForecastResourceId(siteId, startDate, endDate, resolution, columns)),
      SWRKeyEnum.GET_SITE_CAGE_FORECAST,
    ]);

    void mutateAll(SWRKeyEnum.GET_PLAN_FISH_GROUPS);

    // const KPISList = tableContextMetric === MetricsEnum.feedAmount && [KPIColumnEnum.feedAmount];

    void mutateAll(SWRKeyEnum.GET_PLANS_KPIs);
    // mutate([
    //   ...buildSWRKey([[planId], startDate, endDate, KPISList, resolution, filteredSites]),
    //   SWRKeyEnum.GET_PLANS_KPIs,
    // ]);

    void mutateAll(SWRKeyEnum.GET_PLANS_HARVEST_VOLUME);
    // void mutate([
    //   ...buildSWRKey([[planId], startDate, endDate, resolution, filteredSites, species, certificates]),
    //   SWRKeyEnum.GET_PLANS_HARVEST_VOLUME,
    // ]);

    mutateWithDelay([SWRKeyEnum.GET_MAB_UTILIZATION_PER_CONCESSION]);
  }
};

export const refreshHandlerTransferDestiny = ({
  cageIds,
  planId,
  siteId,
  startDate,
  endDate,
  resolution,
}: {
  cageIds: string[];
  planId: string;
  siteId: string;
  startDate: Date;
  endDate: Date;
  resolution: ResolutionEnum;
}) => {
  const columns = [ForecastGetColumnsEnum.Biomass, ForecastGetColumnsEnum.Count, ForecastGetColumnsEnum.Weight];

  void mutate([...buildSWRKey([planId, siteId, startDate, endDate]), SWRKeyEnum.GET_SITE_ACTIONS]);

  void mutate([
    ...buildSWRKey(buildCagesForecastResourceId(cageIds, startDate, endDate, resolution, columns)),
    SWRKeyEnum.GET_SITE_CAGE_FORECAST,
  ]);

  void mutate([
    ...buildSWRKey(buildSiteForecastResourceId(siteId, startDate, endDate, resolution, columns)),
    SWRKeyEnum.GET_SITE_CAGE_FORECAST,
  ]);
};
